import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import { registerSW } from './pwa/registerSW';
import { demoSession, isDemoVendor } from './lib/demoSession';
import { initSentry } from './lib/sentry';
import { hideNativeSplashScreen } from './lib/nativeSplash';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Capacitor } from '@capacitor/core';
import { gatewayContext } from './lib/vendorGateway';
import { VendorGateway } from './components/VendorGateway';
import { nativeEntryContext, nativeEntryRedirect } from './lib/nativeEntry';
import { NativeEntry } from './components/NativeEntry';

const App = lazy(() => import('./App'));

// Safe defaults. Enable each platform only after its linked native release is live.
const routingFlags = {
	ios: import.meta.env.VITE_VENDOR_IOS_ROUTING_ENABLED === 'true',
	android: import.meta.env.VITE_VENDOR_ANDROID_ROUTING_ENABLED === 'true',
};
const nativeEntry = nativeEntryContext(
	window.location.href, navigator.userAgent, navigator.maxTouchPoints,
	Capacitor.isNativePlatform(), import.meta.env.VITE_VENDOR_NATIVE_ENTRY_ENABLED === 'true', routingFlags,
);
// No vendor code in the browser: the marketing site owns the install page.
const marketingRedirect = nativeEntry ? nativeEntryRedirect(nativeEntry) : null;
if (marketingRedirect) window.location.replace(marketingRedirect);
const gateway = gatewayContext(
	window.location.href, navigator.userAgent, navigator.maxTouchPoints,
	Capacitor.isNativePlatform(), routingFlags,
);

// Sentry init runs FIRST — before React mounts and before any user code
// that might throw. The init body is a no-op in dev and a no-op when
// VITE_SENTRY_DSN is unset, so this line is safe in every environment.
if (!nativeEntry && !gateway) initSentry();

// Hide the native splash as early as possible. The iOS shell ships with
// launchAutoHide: false so this JS call is the sole signal that drops the
// splash; the 3000ms launchShowDuration is a backstop, not the typical
// hide path. No-op when running as a plain PWA in the browser.
hideNativeSplashScreen();

// Dev-only: expose demoSession so e2e tests (and ad-hoc devtools probing)
// can drive the per-tab demo CRUD store without dynamic-importing the
// module by URL. Tree-shaken out of production builds because the
// `import.meta.env.DEV` guard is statically false in prod and Rollup
// drops the branch entirely.
if (import.meta.env.DEV) {
	(window as unknown as { __scandeckDemo?: unknown }).__scandeckDemo = {
		demoSession,
		isDemoVendor,
	};
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Missing #root element');

createRoot(rootEl).render(
	<StrictMode>
		<ErrorBoundary scope="root">
			{marketingRedirect ? <p role="status" className="p-6">Opening <a href={marketingRedirect}>scandeckapp.com</a>…</p>
				: nativeEntry ? <NativeEntry context={nativeEntry} />
				: gateway ? <VendorGateway context={gateway} flags={routingFlags} />
					: <Suspense fallback={<p role="status" className="p-6">Loading ScanDeck…</p>}><App /></Suspense>}
		</ErrorBoundary>
	</StrictMode>,
);

if (!nativeEntry && !gateway) registerSW();
